import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { climbVideosService } from '../../services/climb-videos';
import type { ClimbVideo } from '../../types/climb-video';

interface ClimbVideosDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  climbSlug: string;
  climbName: string;
}

/**
 * The videos climbers have put up on a route, playable in place.
 *
 * Plain `<video controls>` rather than the fullscreen viewer the climb profile
 * uses: that one is driven by a `/video/:id` route and belongs to a page a crag
 * route does not have. Here the list *is* the feature — a handful of clips of
 * the same line, watched one after another to work out the beta — so each one
 * plays where it sits, nothing loaded until it is asked for.
 */
export function ClimbVideosDrawer({ open, onOpenChange, climbSlug, climbName }: ClimbVideosDrawerProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'it' ? it : enUS;
  const [videos, setVideos] = useState<ClimbVideo[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    setLoading(true);
    climbVideosService.getClimbVideos(climbSlug)
      .then((data) => { if (!cancelled) setVideos(data); })
      .catch(() => { if (!cancelled) setVideos([]); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [open, climbSlug]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {climbName} — {t('climbVideos.title')} {videos.length > 0 && `(${videos.length})`}
          </SheetTitle>
        </SheetHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">{t('common.loading')}</div>
        ) : videos.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">{t('climbVideos.empty')}</div>
        ) : (
          <div className="mt-4 space-y-4" data-testid="climb-videos-drawer-list">
            {videos.map((video) => (
              <div key={video.id} className="space-y-2">
                <div className="flex items-center gap-2">
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={video.user.avatar_small_url ?? undefined} alt={video.user.username} />
                    <AvatarFallback className="text-[10px]">
                      {video.user.username.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{video.user.username}</span>
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(video.created_at), 'MMM d, yyyy', { locale: dateLocale })}
                  </span>
                </div>
                {video.video_url && (
                  <video
                    src={video.video_url}
                    poster={video.poster_url ?? undefined}
                    controls
                    playsInline
                    preload="none"
                    aria-label={t('climbVideos.openVideo')}
                    className="max-h-[45vh] w-full rounded-lg bg-black"
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
